import React from "react"
import { useTranslation } from "react-i18next"
import { useWindowDimensions, View } from "react-native"

import {
  ContentType,
  SeasonResponse,
  VideoSeriesResponse,
  AppLink as AppLinkType,
  DateDisplayStrategy,
} from "@treefort/api-spec"
import { joinContributorNames } from "@treefort/lib/contributor"
import { formatDate } from "@treefort/lib/date"
import icons from "@treefort/tokens/app/icons"

import useContent from "../../hooks/use-content"
import {
  canDownloadConsumableContent,
  getConsumableContentFromVideoResponse,
  VideoConsumableContent,
} from "../../lib/consumable-content"
import { kebabCase } from "../../lib/kebab-case"
import { spacingToNumber } from "../../lib/spacing"
import { getAbsoluteLineHeight } from "../../lib/text-style"
import { VideoSeriesLayout } from "../../navigation/layouts/module/list-view-modules/video-series"
import { AppLink } from "../app-link"
import ArtworkListItem, {
  TEXT_MARGIN_VERTICAL,
  TEXT_STYLE_TITLE,
  TEXT_GAP_VERTICAL,
  TEXT_STYLE_SUBTITLE,
} from "../artwork-list-item"
import { getBannerHeight } from "../banner"
import BannerImageContainer from "../banner-image-container"
import Box, { BoxPadding } from "../box"
import ButtonView from "../button-view"
import Column from "../column"
import DownloadButton from "../download-button"
import { Heading } from "../heading"
import HorizontalListViewWithArrows from "../horizontal-list-view-with-arrows"
import Icon from "../icon"
import { ListenIconButton } from "../listen-icon-button"
import { MetadataText } from "../metadata"
import { PillGroup, PILL_GROUP_HEIGHT } from "../pill-group"
import ProgressForConsumableContent from "../progress-for-consumable-content"
import Row from "../row"
import { Spacing } from "../spacer"
import Text from "../text"
import TextToggle from "../text-toggle"
import TokensProvider, { ResolvedTokens, useTokens } from "../tokens-provider"
import { SliderItemProgressBar } from "./slider"

const VIDEO_SERIES_BANNER_SIZE = "medium"

const VIDEO_SERIES_DESCRIPTION_MAX_HEIGHT = 76

const VIDEO_SERIES_DESCRIPTION_PADDING_BOTTOM = 16

export function getVideoSeriesModuleBannerDimensions({
  tokens,
  paddingTop,
  displayHeight,
}: {
  tokens: ResolvedTokens
  paddingTop: Spacing
  displayHeight: number
}) {
  const paddingTopNumeric = spacingToNumber(tokens, paddingTop)
  const height =
    paddingTopNumeric +
    getBannerHeight({
      tokens,
      viewportHeight: displayHeight - paddingTopNumeric,
      size: VIDEO_SERIES_BANNER_SIZE,
    })

  return { height }
}

export function getVideoSeriesModuleDescriptionDimensions() {
  return {
    height:
      VIDEO_SERIES_DESCRIPTION_MAX_HEIGHT +
      VIDEO_SERIES_DESCRIPTION_PADDING_BOTTOM,
  }
}

export function getVideoSeriesModuleSeasonPillGroupDimensions({
  tokens,
}: {
  tokens: ResolvedTokens
}) {
  return {
    height:
      PILL_GROUP_HEIGHT + tokens.videoSeriesModule.seasonPillGroupPaddingBottom,
  }
}

export function getVideoSeriesModuleEpisodeDimensions({
  tokens,
  layout,
  windowDimensions,
  paddingLeft,
  paddingRight,
  hasDescription,
}: {
  tokens: ResolvedTokens
  windowDimensions: { height: number; width: number }
  layout: VideoSeriesLayout
  paddingLeft: number
  paddingRight: number
  hasDescription: boolean
}) {
  const aspectRatio = tokens.artworkListItem.shape.wide.aspectRatio
  const textHeight =
    TEXT_MARGIN_VERTICAL +
    getAbsoluteLineHeight(TEXT_STYLE_TITLE, tokens) +
    (hasDescription
      ? TEXT_GAP_VERTICAL +
        getAbsoluteLineHeight(TEXT_STYLE_SUBTITLE, tokens) *
          tokens.videoSeriesModule.episodeDescriptionNumberOfLines[layout]
      : 0) +
    TEXT_MARGIN_VERTICAL
  const buttonsHeight = tokens.icon.size.xlarge
  const artworkHeight =
    layout === "verticalNarrow"
      ? (windowDimensions.width - paddingLeft - paddingRight) / aspectRatio
      : tokens.videoSeriesModule.fixedArtworkHeight
  const artworkWidth =
    layout === "verticalNarrow"
      ? windowDimensions.width - paddingLeft - paddingRight
      : Math.round(artworkHeight * aspectRatio)

  let height = 0
  switch (layout) {
    case "verticalNarrow":
    case "horizontal":
      height = artworkHeight + textHeight + buttonsHeight
      break
    case "verticalWide":
      height = artworkHeight
      break
    default:
      layout as never
  }

  return {
    artworkWidth,
    artworkHeight,
    height,
    textHeight,
  }
}

export function VideoSeriesModuleBanner({
  videoSeries,
  seasonIndex,
  currentEpisode,
  isSeriesStarted,
  paddingTop = "none",
  paddingLeft,
  paddingRight,
  layout,
}: {
  videoSeries: VideoSeriesResponse
  seasonIndex: number
  currentEpisode: SeasonResponse["episodes"][number]
  isSeriesStarted: boolean
  paddingTop?: BoxPadding
  paddingLeft: number
  paddingRight: number
  layout: VideoSeriesLayout
}) {
  const { tokens, displayHeight } = useTokens()
  const { t } = useTranslation()

  const isVerticalNarrow = layout === "verticalNarrow"
  const publisher = joinContributorNames(videoSeries.contributors, "publisher")
  const { height } = getVideoSeriesModuleBannerDimensions({
    tokens,
    displayHeight,
    paddingTop,
  })

  const playButtonText = isSeriesStarted
    ? t("Continue watching")
    : t("Play now")

  const bannerContent = (
    <>
      <View>
        <Heading
          textStyle="headingXLarge"
          shadow
          level={1}
          color="highContrast"
          maxWidth="title"
        >
          {videoSeries.title}
        </Heading>
        {publisher ? <MetadataText>{publisher}</MetadataText> : null}
      </View>
      <AppLink
        style={{
          width: isVerticalNarrow ? "100%" : undefined,
        }}
        id={kebabCase(
          `videoseries-play-${videoSeries.id}-${videoSeries.title}`,
        )}
        to={{
          type: "content",
          contentType: "video" as ContentType,
          contentId: currentEpisode.id,
          params: { autoplay: "1" },
        }}
        feedback="ripple-or-opacity"
        aria-label={playButtonText}
      >
        <ButtonView icon={icons.play} type="primary">
          {playButtonText}
        </ButtonView>
      </AppLink>
    </>
  )

  return (
    <TokensProvider displayMode="dark">
      <BannerImageContainer
        source={{
          uri:
            videoSeries.details.seasons[seasonIndex].artworkMedia?.original
              .url ||
            videoSeries.details.backgroundMedia?.original.url ||
            videoSeries.artworkMedia?.original.url,
        }}
        paddingBottom="medium"
        height={height}
        gradientOffset={tokens.videoSeriesModule.banner.gradientOffset}
        gradientCoverage={tokens.videoSeriesModule.banner.gradientCoverage}
        backgroundColor={tokens.videoSeriesModule.banner.backgroundColor}
      >
        <Column
          paddingLeft={paddingLeft - tokens.spacing.pagePaddingHorizontal}
          paddingRight={paddingRight - tokens.spacing.pagePaddingHorizontal}
          justifyContent="flex-end"
          alignItems="flex-start"
          gap="large"
          paddingTop="yuge"
          paddingBottom="small"
        >
          {bannerContent}
        </Column>
      </BannerImageContainer>
    </TokensProvider>
  )
}

export function VideoSeriesModuleDescription({
  videoSeries,
  seasonIndex,
  paddingLeft,
  paddingRight,
  layout,
}: {
  videoSeries: VideoSeriesResponse
  seasonIndex: number
  paddingLeft: number
  paddingRight: number
  layout: VideoSeriesLayout
}) {
  const { tokens } = useTokens()
  const windowDimensions = useWindowDimensions()

  const { artworkWidth: episodeArtworkWidth } =
    getVideoSeriesModuleEpisodeDimensions({
      tokens,
      layout,
      paddingLeft,
      paddingRight,
      windowDimensions,
      hasDescription: videoSeries.details.seasons[seasonIndex].episodes.some(
        (episode) => episode.description !== undefined,
      ),
    })
  const descriptionMaxWidth =
    tokens.text.maxWidth.description +
    (layout === "verticalWide"
      ? // Align the right edge of the series and episode
        // descriptions in the verticalWide layout
        tokens.spacing.large + episodeArtworkWidth
      : 0)

  return videoSeries.description ? (
    <TokensProvider displayMode="dark">
      <Box
        backgroundColor="primary"
        paddingLeft={paddingLeft}
        paddingRight={paddingRight}
        paddingBottom={VIDEO_SERIES_DESCRIPTION_PADDING_BOTTOM}
      >
        <TextToggle
          title={videoSeries.title}
          textStyle="body"
          type="modal"
          maxHeight={VIDEO_SERIES_DESCRIPTION_MAX_HEIGHT}
          maxWidth={descriptionMaxWidth}
          maxAllowedHeadingLevel={2}
        >
          {videoSeries.description}
        </TextToggle>
      </Box>
    </TokensProvider>
  ) : null
}

export function VideoSeriesModuleSeasonPillGroup({
  videoSeries,
  paddingLeft,
  paddingRight,
  seasonIndex,
  onChangeSeasonIndex,
}: {
  videoSeries: VideoSeriesResponse
  paddingLeft: number
  paddingRight: number
  seasonIndex: number
  onChangeSeasonIndex: (index: number) => void
}) {
  const { tokens } = useTokens()
  return (
    <TokensProvider displayMode="dark">
      <Box
        backgroundColor="primary"
        paddingBottom={tokens.videoSeriesModule.seasonPillGroupPaddingBottom}
      >
        <PillGroup
          selectionBehavior="radiobuttons"
          id={`${videoSeries.id}-season-selector`}
          pills={videoSeries.details.seasons
            .map((season, i) => ({
              id: season.id.toString(),
              label: season.title,
              value: i,
            }))
            .reverse()}
          selectedValues={[seasonIndex]}
          onSelectionChange={([selection]) => onChangeSeasonIndex(selection)}
          paddingLeft={paddingLeft}
          paddingRight={paddingRight}
        />
      </Box>
    </TokensProvider>
  )
}

export function VideoSeriesModuleHorizontalEpisodesList({
  episodes,
  paddingLeft,
  paddingRight,
  dateDisplayStrategy,
}: {
  episodes: SeasonResponse["episodes"]
  paddingLeft: number
  paddingRight: number
  dateDisplayStrategy: DateDisplayStrategy | null
}) {
  const { tokens, displayWidth } = useTokens()
  const windowDimensions = useWindowDimensions()

  const episodeDimensions = getVideoSeriesModuleEpisodeDimensions({
    tokens,
    windowDimensions,
    paddingLeft,
    paddingRight,
    layout: "horizontal",
    hasDescription: episodes.some(
      (episode) => episode.description !== undefined,
    ),
  })

  // Rerender the ListView when the selected season changes
  const key = episodes.map((episode) => episode.id).join("-")

  return (
    <HorizontalListViewWithArrows
      key={key}
      id={key}
      getGapSize={() => tokens.spacing.large}
      paddingStart={paddingLeft}
      paddingEnd={paddingRight}
      viewSize={{ height: episodeDimensions.height, width: displayWidth }}
      getItemKey={(episode) => episode.id.toString()}
      items={episodes}
      getItemSize={() => episodeDimensions.artworkWidth}
      arrowButtonHeight={episodeDimensions.artworkHeight}
      renderItem={(episode) => (
        <VideoSeriesModuleEpisode
          dateDisplayStrategy={dateDisplayStrategy}
          episode={episode}
          paddingLeft={0}
          paddingRight={0}
          layout="horizontal"
        />
      )}
    />
  )
}

export function VideoSeriesModuleEpisode({
  episode,
  paddingLeft,
  paddingRight,
  layout,
  dateDisplayStrategy,
}: {
  episode: SeasonResponse["episodes"][number]
  paddingLeft: number
  paddingRight: number
  layout: VideoSeriesLayout
  dateDisplayStrategy: DateDisplayStrategy | null
}) {
  const { tokens } = useTokens()
  const video = useContent(episode.id, "video")
  const windowDimensions = useWindowDimensions()
  const { i18n } = useTranslation()

  const dimensions = getVideoSeriesModuleEpisodeDimensions({
    tokens,
    layout,
    paddingLeft,
    paddingRight,
    windowDimensions,
    hasDescription: episode.description !== undefined,
  })
  const link: AppLinkType = {
    type: "content",
    contentId: episode.id,
    contentType: "video",
  }

  const formattedDate =
    dateDisplayStrategy !== "hidden" && episode.publishedAt ? (
      <Text textStyle="caption" color="secondary">
        {formatDate(new Date(episode.publishedAt), {
          strategy: episode.dateDisplayStrategy || "fullDate",
          i18n,
        })}
      </Text>
    ) : null

  return layout === "verticalNarrow" || layout === "horizontal" ? (
    <Box key={episode.id} paddingLeft={paddingLeft} paddingRight={paddingRight}>
      <ArtworkListItem
        withoutRightSpacing
        withoutBottomSpacing
        artworkShape="wide"
        titleNumberOfLines={1}
        subtitleNumberOfLines={
          tokens.videoSeriesModule.episodeDescriptionNumberOfLines[layout]
        }
        height={dimensions.artworkHeight + dimensions.textHeight}
        artworkDimensions={{
          width: dimensions.artworkWidth,
          height: dimensions.artworkHeight,
        }}
        data={{
          link,
          isLoading: false,
          artwork: episode.artworkMedia?.original.url,
          title: episode.title,
          subtitle: episode.description,
        }}
        progressBar={<SliderItemProgressBar contentLink={link} />}
      />
      <Row justifyContent="space-between">
        <ButtonRow
          consumableContent={
            video.data
              ? getConsumableContentFromVideoResponse(video.data)
              : undefined
          }
        />
        {formattedDate}
      </Row>
    </Box>
  ) : (
    <Row
      gap="large"
      alignItems="stretch"
      paddingLeft={paddingLeft}
      paddingRight={paddingRight}
      height={dimensions.artworkHeight}
    >
      <ArtworkListItem
        artworkShape="wide"
        withoutBottomSpacing
        withoutRightSpacing
        titleNumberOfLines={0}
        artworkDimensions={{
          height: dimensions.artworkHeight,
          width: dimensions.artworkWidth,
        }}
        data={{
          isLoading: false,
          artwork: episode.artworkMedia?.original.url,
          title: episode.title,
          subtitle: episode.description,
          link,
        }}
        progressBar={<SliderItemProgressBar contentLink={link} />}
      />
      <Column
        flex={1}
        alignItems="flex-start"
        justifyContent="space-between"
        paddingVertical="xsmall"
        maxHeight="100%"
      >
        <Column gap="xsmall" alignItems="flex-start" maxWidth="100%">
          <Box width={tokens.text.maxWidth.description} maxWidth="100%">
            <Text textStyle="headingLarge" numberOfLines={1}>
              {episode.title}
            </Text>
          </Box>
          {formattedDate}
          {episode.description ? (
            <Text
              maxWidth="description"
              textStyle="body"
              numberOfLines={
                tokens.videoSeriesModule.episodeDescriptionNumberOfLines[layout]
              }
            >
              {episode.description}
            </Text>
          ) : null}
        </Column>
        <ButtonRow
          consumableContent={
            video.data
              ? getConsumableContentFromVideoResponse(video.data)
              : undefined
          }
        />
      </Column>
    </Row>
  )
}

function ButtonRow({
  consumableContent,
}: {
  consumableContent: VideoConsumableContent | undefined
}) {
  const { t } = useTranslation()
  const { tokens } = useTokens()

  const content = consumableContent?.content

  return (
    <Row gap="xsmall" alignItems="center" height={tokens.icon.size.xlarge}>
      {content ? (
        <AppLink
          id={kebabCase(`play-video-series-episode-${content.id}`)}
          to={{
            type: "content",
            contentType: "video" as ContentType,
            contentId: content.id,
            params: { autoplay: "1" },
          }}
          feedback="opacity"
          aria-label={t("Play now")}
        >
          <Icon size="xlarge" source={icons.playCircle.fill} />
        </AppLink>
      ) : (
        <Icon size="xlarge" source={icons.playCircle.fill} />
      )}
      <Row gap="none">
        {consumableContent &&
        canDownloadConsumableContent(consumableContent) ? (
          <DownloadButton consumableContent={consumableContent} />
        ) : null}
        {content?.details.videoMedia?.status === "available" ? (
          <ListenIconButton consumableContent={consumableContent} />
        ) : null}
      </Row>
      <ProgressForConsumableContent
        consumableContent={consumableContent}
        includeDurationLabel
        includeFinishedBadge
      />
    </Row>
  )
}
